import * as React from "react";
import { Upload, Image as ImageIcon, Video } from 'lucide-react';
import { WizardData } from '../components/PublishingWizard';
import CardComponent from '../ui/CardComponent';

interface WizardStep1Props {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
}

const PLATFORMS = [
  { id: 'Instagram', name: 'Instagram', color: 'bg-pink-500' },
  { id: 'Facebook', name: 'Facebook', color: 'bg-blue-600' },
  { id: 'YouTube', name: 'YouTube', color: 'bg-red-600' },
  { id: 'LinkedIn', name: 'LinkedIn', color: 'bg-blue-700' },
  { id: 'Twitter', name: 'Twitter', color: 'bg-sky-500' },
  { id: 'TikTok', name: 'TikTok', color: 'bg-black' },
];

export default function WizardStep1({ data, updateData }: WizardStep1Props) {
  const handlePlatformToggle = (platformId: string) => {
    const platforms = data.platforms.includes(platformId)
      ? data.platforms.filter((p) => p !== platformId)
      : [...data.platforms, platformId];
    updateData({ platforms });
  };

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      updateData({ thumbnail: e.target.files[0] });
    }
  };

  return (
    <div className="space-y-8">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">
          Post Title *
        </label>
        <input
          type="text"
          value={data.title}
          onChange={(e) => updateData({ title: e.target.value })}
          placeholder="Enter post title..."
          className="w-full h-10 px-3 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#FED7D5] transition-all"
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">
          Description / Caption
        </label>
        <textarea
          value={data.description}
          onChange={(e) => updateData({ description: e.target.value })}
          placeholder="Write your caption or description..."
          rows={4}
          className="w-full px-3 py-2 text-sm border border-[#D1D5DB] rounded-md focus:outline-none focus:border-[#F67570] focus:ring-2 focus:ring-[#FED7D5] resize-none transition-all"
        />
      </div>

      {/* Platform Selector */}
      <div>
        <label className="block text-sm font-medium text-[#111827] mb-3">
          Select Platforms *
        </label>
        <div className="flex flex-wrap gap-3">
          {PLATFORMS.map((platform) => (
            <button
              key={platform.id}
              onClick={() => handlePlatformToggle(platform.id)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full border transition-all ${
                data.platforms.includes(platform.id)
                  ? 'border-[#2563EB] bg-[#2563EB] text-white shadow-sm'
                  : 'border-[#D1D5DB] bg-white text-[#6B7280] hover:border-[#9CA3AF] hover:bg-[#F9FAFB]'
              }`}
            >
              {platform.name}
            </button>
          ))}
        </div>
      </div>

      {/* Media Type */}
      <div>
        <label className="block text-sm font-medium text-[#111827] mb-3">
          Media Type
        </label>
        <div className="flex gap-4">
          <button
            onClick={() => updateData({ mediaType: 'image' })}
            className={`flex-1 p-6 border-2 rounded-lg transition-all ${
              data.mediaType === 'image'
                ? 'border-[#2563EB] bg-[#EFF6FF] shadow-sm'
                : 'border-[#E5E7EB] bg-white hover:border-[#9CA3AF] hover:bg-[#F9FAFB]'
            }`}
          >
            <ImageIcon
              size={32}
              className={`mx-auto mb-2 ${
                data.mediaType === 'image' ? 'text-[#2563EB]' : 'text-[#6B7280]'
              }`}
            />
            <div className="text-sm font-medium text-[#111827]">Image</div>
          </button>
          <button
            onClick={() => updateData({ mediaType: 'video' })}
            className={`flex-1 p-6 border-2 rounded-lg transition-all ${
              data.mediaType === 'video'
                ? 'border-[#2563EB] bg-[#EFF6FF] shadow-sm'
                : 'border-[#E5E7EB] bg-white hover:border-[#9CA3AF] hover:bg-[#F9FAFB]'
            }`}
          >
            <Video
              size={32}
              className={`mx-auto mb-2 ${
                data.mediaType === 'video' ? 'text-[#2563EB]' : 'text-[#6B7280]'
              }`}
            />
            <div className="text-sm font-medium text-[#111827]">Video</div>
          </button>
        </div>
      </div>

      {/* Thumbnail Upload */}
      <CardComponent padding="none">
        <div className="p-6">
          <label className="block text-sm font-medium text-[#111827] mb-3">
            Thumbnail
          </label>
          <div className="border-2 border-dashed border-[#E5E7EB] rounded-lg p-8 text-center hover:border-[#9CA3AF] transition-colors">
            <Upload size={32} className="mx-auto text-[#9CA3AF] mb-3" />
            <p className="text-sm text-[#6B7280] mb-2">
              {data.thumbnail ? data.thumbnail.name : 'Click to upload or drag and drop'}
            </p>
            <p className="text-xs text-[#9CA3AF]">PNG, JPG up to 10MB</p>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              className="hidden"
              id="thumbnail-upload"
            />
            <label
              htmlFor="thumbnail-upload"
              className="inline-block mt-4 px-4 py-2 text-sm font-medium text-[#2563EB] border border-[#2563EB] rounded-md hover:bg-[#EFF6FF] cursor-pointer transition-colors"
            >
              Choose File
            </label>
          </div>
        </div>
      </CardComponent>

      {/* Media Preview */}
      {data.mediaFiles.length > 0 && (
        <CardComponent padding="none">
          <div className="p-6">
            <div className="text-sm font-medium text-[#111827] mb-3">Media Preview</div>
            <div className="grid grid-cols-3 gap-4">
              {data.mediaFiles.map((file, index) => (
                <div
                  key={index}
                  className="aspect-square bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg flex items-center justify-center"
                >
                  {data.mediaType === 'image' ? (
                    <ImageIcon size={32} className="text-[#9CA3AF]" />
                  ) : (
                    <Video size={32} className="text-[#9CA3AF]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardComponent>
      )}
    </div>
  );
}